/**
 * Mobile Lane Panel — per-user mobile subscriptions with operator grant/revoke.
 * Mobile plans are USER-scoped (separate from the application's web subscription).
 */
import React, { useCallback, useEffect, useState } from 'react'
import { CircularProgress } from '@mui/joy'
import { PhoneIphone } from '@mui/icons-material'
import { BillingPeriod, MobilePlan } from '@numeo/types-core'
import dayjs from 'dayjs'
import { billingApi } from './api'
import { CustomerUser, MobileRevokeMode } from './types'

interface MobileLanePanelProps {
    appId: string
    users: CustomerUser[]
    onSuccess: (message: string) => void
    onError: (message: string) => void
    onRefresh: () => Promise<void>
}

export default function MobileLanePanel({ appId, users, onSuccess, onError, onRefresh }: MobileLanePanelProps) {
    // Mobile grants are user-scoped, so the operator picks a target user before assigning.
    const [mobileUserId, setMobileUserId] = useState('')
    const [mobileTarget, setMobileTarget] = useState<MobilePlan>(MobilePlan.PRO_MOBILE)
    const [mobileCycle, setMobileCycle] = useState<BillingPeriod>('monthly')
    const [mobileDays, setMobileDays] = useState('')
    const [mobileBusy, setMobileBusy] = useState(false)

    // Default the grant target to the first user so the operator never submits against an empty selection.
    useEffect(() => {
        if (!mobileUserId && users.length) setMobileUserId(users[0].userId)
    }, [users, mobileUserId])

    const assignMobile = useCallback(async () => {
        const days = parseInt(mobileDays, 10)
        if (!mobileUserId || !days || days < 1) return
        setMobileBusy(true)
        try {
            await billingApi.assignMobilePlan(appId, mobileUserId, { plan: mobileTarget, cycle: mobileCycle, trialDays: days })
            onSuccess(`${mobileTarget} granted for ${days} days`)
            setMobileDays('')
            await onRefresh()
        } catch (err: unknown) {
            const axiosErr = err as { response?: { data?: { message?: string } } }
            onError(axiosErr.response?.data?.message || 'Failed to grant mobile plan')
        } finally {
            setMobileBusy(false)
        }
    }, [appId, mobileUserId, mobileTarget, mobileCycle, mobileDays, onSuccess, onError, onRefresh])

    const revokeMobile = useCallback(
        async (userId: string, mode: MobileRevokeMode) => {
            // An immediate revoke cuts the user's entitlement off mid-session, so make it deliberate.
            // The deferred mode is non-destructive (access runs to period end) and needs no prompt.
            if (mode === 'immediate' && !window.confirm('Revoke this mobile plan immediately? The user loses access right away.')) return
            setMobileBusy(true)
            try {
                await billingApi.revokeMobilePlan(appId, userId, mode)
                onSuccess(mode === 'immediate' ? 'Mobile plan revoked' : 'Mobile plan will end at period end')
                await onRefresh()
            } catch (err: unknown) {
                const axiosErr = err as { response?: { data?: { message?: string } } }
                onError(axiosErr.response?.data?.message || 'Failed to revoke mobile plan')
            } finally {
                setMobileBusy(false)
            }
        },
        [appId, onSuccess, onError, onRefresh]
    )

    return (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm mb-6">
            <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2">
                <div className="w-7 h-7 rounded-md bg-teal-50 flex items-center justify-center">
                    <PhoneIphone sx={{ fontSize: 14 }} className="text-teal-600" />
                </div>
                <div>
                    <p className="text-sm font-semibold text-gray-900">Mobile Plans</p>
                    <p className="text-xs text-gray-400">Per-user mobile tiers — granted as a trial, separate from the web subscription</p>
                </div>
            </div>

            <div className="p-5">
                {users.length === 0 ? (
                    <p className="text-sm text-gray-400">No active users on this application</p>
                ) : (
                    <>
                        {/* Current mobile subscriptions */}
                        <div className="mb-4 space-y-1.5">
                            {users.map((u) => {
                                const m = u.mobileSubscription
                                return (
                                    <div key={u.userId} className="flex items-center justify-between px-3 py-2 rounded-md bg-gray-50 text-xs">
                                        <span className="text-gray-700 truncate mr-3">{u.email}</span>
                                        {m ? (
                                            <div className="flex items-center gap-2 shrink-0">
                                                <span
                                                    className={`font-bold px-2 py-0.5 rounded ${m.plan === MobilePlan.PLATINUM_MOBILE ? 'bg-amber-100 text-amber-700' : m.plan === MobilePlan.ULTRA_MOBILE ? 'bg-violet-100 text-violet-700' : 'bg-blue-100 text-blue-700'}`}
                                                >
                                                    {m.plan}
                                                </span>
                                                <span className="text-gray-500">{m.status}</span>
                                                {m.cycle && <span className="text-gray-400">{m.cycle}</span>}
                                                {m.currentPeriodEnd && (
                                                    <span className="text-gray-400">
                                                        {m.cancelAtPeriodEnd ? 'ends' : 'renews'} {dayjs(m.currentPeriodEnd).format('MMM D, YYYY')}
                                                    </span>
                                                )}
                                                {m.cancelAtPeriodEnd && <span className="text-amber-600">revoke pending</span>}
                                                {/* Revoke is only valid on comped grants — a customer-paid sub is cancelled by the
                                                    user via the Customer Portal, and the server 409s if we tried anyway. */}
                                                {m.isGrant ? (
                                                    <>
                                                        <button
                                                            disabled={mobileBusy}
                                                            onClick={() => void revokeMobile(u.userId, 'immediate')}
                                                            className="text-red-600 border border-red-200 rounded px-2 py-0.5 hover:bg-red-50 disabled:opacity-50 transition-colors"
                                                        >
                                                            Revoke now
                                                        </button>
                                                        {!m.cancelAtPeriodEnd && (
                                                            <button
                                                                disabled={mobileBusy}
                                                                onClick={() => void revokeMobile(u.userId, 'period_end')}
                                                                className="text-gray-600 border border-gray-200 rounded px-2 py-0.5 hover:bg-gray-100 disabled:opacity-50 transition-colors"
                                                            >
                                                                At period end
                                                            </button>
                                                        )}
                                                    </>
                                                ) : (
                                                    <span className="text-gray-400">customer-paid</span>
                                                )}
                                            </div>
                                        ) : (
                                            <span className="text-gray-400 shrink-0">No mobile plan</span>
                                        )}
                                    </div>
                                )
                            })}
                        </div>

                        {/* Grant a mobile plan */}
                        <div className="flex flex-wrap items-center gap-3 pt-3 border-t border-gray-100">
                            <select
                                value={mobileUserId}
                                onChange={(e) => setMobileUserId(e.target.value)}
                                className="h-8 text-xs px-2 bg-white border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500/20 max-w-[220px]"
                            >
                                {users.map((u) => (
                                    <option key={u.userId} value={u.userId}>
                                        {u.email}
                                    </option>
                                ))}
                            </select>
                            <div className="flex gap-1.5">
                                {[MobilePlan.PRO_MOBILE, MobilePlan.ULTRA_MOBILE, MobilePlan.PLATINUM_MOBILE].map((p) => (
                                    <button
                                        key={p}
                                        onClick={() => setMobileTarget(p)}
                                        className={`h-8 px-3 text-xs font-bold rounded-md transition-all ${mobileTarget === p ? 'bg-teal-600 text-white shadow-sm' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-100'}`}
                                    >
                                        {p.replace('_MOBILE', '')}
                                    </button>
                                ))}
                            </div>
                            <div className="flex gap-1.5">
                                {(['monthly', 'annual'] as BillingPeriod[]).map((c) => (
                                    <button
                                        key={c}
                                        onClick={() => setMobileCycle(c)}
                                        className={`h-8 px-3 text-xs font-medium rounded-md transition-all ${mobileCycle === c ? 'bg-gray-800 text-white shadow-sm' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-100'}`}
                                    >
                                        {c}
                                    </button>
                                ))}
                            </div>
                            <div className="flex items-center gap-1.5">
                                <input
                                    type="number"
                                    placeholder="Days"
                                    value={mobileDays}
                                    onChange={(e) => setMobileDays(e.target.value)}
                                    className="h-8 w-20 text-sm px-2 bg-white border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500/20"
                                />
                                <span className="text-xs text-gray-500">days</span>
                            </div>
                            <button
                                disabled={mobileBusy || !mobileUserId || !mobileDays || parseInt(mobileDays, 10) < 1}
                                onClick={() => void assignMobile()}
                                className="h-8 px-4 text-xs font-medium bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:opacity-50 transition-colors"
                            >
                                {mobileBusy ? <CircularProgress size="sm" sx={{ '--CircularProgress-size': '12px' }} /> : 'Grant'}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    )
}
